import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ImagePlus, Send, X } from "lucide-react";
import { api, formatApiError } from "../lib/api";

const MAX_SIZE = 40 * 1024 * 1024;

const Upload = () => {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [dogName, setDogName] = useState("");
  const [caption, setCaption] = useState("");
  const [birthDate, setBirthDate] = useState("");
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  const pickFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > MAX_SIZE) {
      toast.error("File troppo grande: massimo 40 MB.");
      return;
    }
    setFile(f);
    setPreview(URL.createObjectURL(f));
  };

  const clearFile = () => {
    if (preview) URL.revokeObjectURL(preview);
    setFile(null);
    setPreview(null);
    if (inputRef.current) inputRef.current.value = "";
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!file) {
      toast.error("Scegli prima una foto o un video.");
      return;
    }
    setBusy(true);
    try {
      const data = new FormData();
      data.append("file", file);
      data.append("dog_name", dogName);
      data.append("caption", caption);
      data.append("birth_date", birthDate);
      await api.post("/posts", data, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Ricordo inviato!", {
        description: "Sarà pubblicato nella galleria dopo l'approvazione dei fondatori.",
      });
      navigate("/");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const inputCls =
    "w-full rounded-xl border border-[#E8DFD5] bg-[#FBF8F3] px-4 py-3.5 text-sm text-[#2C2723] outline-none transition-all duration-300 placeholder:text-[#8C7B6E]/60 focus:border-[#B85328] focus:ring-2 focus:ring-[#B85328]/15";

  return (
    <main className="mx-auto max-w-2xl px-4 py-14 sm:py-20">
      <p className="eyebrow flex items-center gap-3">
        <span className="inline-block h-px w-8 bg-[#B85328]" />
        Condividi
      </p>
      <h1 data-testid="upload-heading" className="font-display mt-4 text-3xl font-semibold text-[#2C2723] sm:text-4xl lg:text-5xl">
        La nuova vita del tuo <span className="italic text-[#B85328]">bassotto</span>
      </h1>
      <p className="mt-4 text-sm leading-relaxed text-[#8C7B6E]">
        Carica una foto o un video: dopo l'approvazione dei fondatori sarà visibile a tutta la community.
      </p>

      <form data-testid="upload-form" onSubmit={submit} className="mt-10 space-y-6 rounded-3xl border border-[#E8DFD5] bg-white p-7 shadow-sm sm:p-9">
        {preview ? (
          <div className="relative overflow-hidden rounded-2xl border border-[#E8DFD5]">
            {file.type.startsWith("video/") ? (
              <video data-testid="upload-preview-video" src={preview} controls className="max-h-96 w-full object-cover" />
            ) : (
              <img data-testid="upload-preview-image" src={preview} alt="Anteprima" className="max-h-96 w-full object-cover" />
            )}
            <button
              type="button"
              data-testid="upload-clear-file-button"
              onClick={clearFile}
              className="absolute right-3 top-3 rounded-full bg-[#1F1A17]/80 p-2 text-[#FBF8F3] transition-colors hover:bg-[#B85328]"
              aria-label="Rimuovi file"
            >
              <X size={16} />
            </button>
          </div>
        ) : (
          <button
            type="button"
            data-testid="upload-file-picker"
            onClick={() => inputRef.current?.click()}
            className="flex w-full flex-col items-center gap-3 rounded-2xl border-2 border-dashed border-[#CFC0B2] bg-[#FBF8F3] py-14 transition-all duration-300 hover:border-[#B85328] hover:bg-[#F5EFE6]"
          >
            <span className="flex h-14 w-14 items-center justify-center rounded-full bg-[#B85328]/10 text-[#B85328]">
              <ImagePlus size={24} />
            </span>
            <span className="text-sm font-semibold text-[#2C2723]">Scegli una foto o un video</span>
            <span className="text-xs text-[#8C7B6E]">JPG, PNG, WEBP, MP4 o WEBM — max 40 MB</span>
          </button>
        )}
        <input
          ref={inputRef}
          data-testid="upload-file-input"
          type="file"
          accept="image/jpeg,image/png,image/webp,video/mp4,video/webm,video/quicktime"
          onChange={pickFile}
          className="hidden"
        />

        <div>
          <label htmlFor="dog-name" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.2em] text-[#8C7B6E]">
            Nome del tuo bassotto
          </label>
          <input
            id="dog-name"
            data-testid="upload-input-dog-name"
            required
            value={dogName}
            onChange={(e) => setDogName(e.target.value)}
            placeholder="Es. Olmo"
            className={inputCls}
          />
        </div>
        <div>
          <label htmlFor="birth-date" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.2em] text-[#8C7B6E]">
            Data di nascita del bassotto (facoltativo)
          </label>
          <input
            id="birth-date"
            data-testid="upload-input-birth-date"
            type="date"
            value={birthDate}
            max={new Date().toISOString().slice(0, 10)}
            onChange={(e) => setBirthDate(e.target.value)}
            className={inputCls}
          />
          <p className="mt-2 text-xs text-[#8C7B6E]">Il giorno del compleanno riceverai un augurio dalla Casetta.</p>
        </div>
        <div>
          <label htmlFor="caption" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.2em] text-[#8C7B6E]">
            Racconta il momento (facoltativo)
          </label>
          <textarea
            id="caption"
            data-testid="upload-input-caption"
            rows={4}
            value={caption}
            onChange={(e) => setCaption(e.target.value)}
            placeholder="La prima corsa in giardino, il nuovo divano preferito..."
            className={`${inputCls} resize-none`}
          />
        </div>
        <button
          type="submit"
          data-testid="upload-submit-button"
          disabled={busy}
          className="flex w-full items-center justify-center gap-2 rounded-full bg-[#B85328] py-4 text-sm font-semibold text-[#FBF8F3] transition-all duration-300 hover:-translate-y-0.5 hover:bg-[#9C421C] hover:shadow-xl disabled:opacity-60"
        >
          {busy ? "Invio in corso..." : "Invia per l'approvazione"}
          <Send size={15} />
        </button>
      </form>
    </main>
  );
};

export default Upload;
